import React from 'react';
import { X, Clock, Trash2 } from 'lucide-react';
import { HealthEntry } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import { STOOL_COLORS } from '../constants';

interface LogDetailModalProps {
  selectedLog: HealthEntry | null;
  onClose: () => void;
  onDelete: (id: string) => void;
  formatDateFR: (date: string) => string;
  formatTimeFR: (date: string) => string;
}

export const LogDetailModal: React.FC<LogDetailModalProps> = ({
  selectedLog,
  onClose,
  onDelete,
  formatDateFR,
  formatTimeFR
}) => {
  const { t } = useTranslation();

  if (!selectedLog) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[110] flex items-end p-4 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md mx-auto p-8 rounded-t-[3rem] animate-slide-up relative shadow-2xl">
        <h3 className="text-2xl font-black mb-1">{t('app:tracking.detail_title')}</h3>
        <div className="flex items-center gap-2 mb-6">
          <p className="text-indigo-600 font-bold">{formatDateFR(selectedLog.date)}</p>
          <span className="text-gray-300">•</span>
          <p className="text-gray-400 font-bold text-sm flex items-center gap-1">
            <Clock size={12}/>{formatTimeFR(selectedLog.date)}
          </p>
        </div>
        <button onClick={onClose} className="absolute top-8 right-8 text-gray-300 hover:text-gray-500">
          <X />
        </button>
        
        <div className="space-y-4 overflow-y-auto max-h-[60vh] pb-4 hide-scrollbar">
          <div className="flex gap-4">
            <div className="flex-1 bg-gray-50 p-4 rounded-2xl">
              <p className="text-[10px] font-black uppercase text-gray-400 mb-2">{t('app:tracking.weight')}</p>
              <p className="text-xl font-black text-gray-900">
                {selectedLog.weight ? selectedLog.weight : '--'} <span className="text-sm text-gray-400">kg</span>
              </p>
            </div>
            <div className="flex-1 bg-gray-50 p-4 rounded-2xl">
              <p className="text-[10px] font-black uppercase text-gray-400 mb-2">{t('app:tracking.mood')}</p>
              <p className="text-xl font-black text-gray-900 capitalize">
                {selectedLog.mood ? t(`app:moods.${selectedLog.mood}`) : '--'}
              </p>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-2xl">
            <p className="text-[10px] font-black uppercase text-gray-400 mb-2">{t('app:tracking.appetite')}</p>
            <p className="font-bold text-gray-900">
              {selectedLog.appetite ? t(`app:appetite.${selectedLog.appetite}`) : '--'}
            </p>
          </div>
          
          {(selectedLog.stoolScore || selectedLog.stoolColor) && (
            <div className="bg-orange-50 p-4 rounded-2xl border border-orange-100">
              <p className="text-[10px] font-black uppercase text-orange-400 mb-2">{t('app:tracking.stool_quality')}</p>
              <div className="space-y-1">
                {selectedLog.stoolScore && (
                  <p className="font-bold text-orange-900">
                    {selectedLog.stoolScore} - {t(`app:stool_scores.${selectedLog.stoolScore}`)}
                  </p>
                )}
                {selectedLog.stoolColor && (
                  <div className="flex items-center gap-2 mt-2">
                    <div 
                      className="w-4 h-4 rounded-full border border-black/10 shadow-sm" 
                      style={{ 
                        backgroundColor: STOOL_COLORS.find(c => c.value === selectedLog.stoolColor)?.color || '#ccc' 
                      }}
                    />
                    <p className="text-sm font-semibold text-gray-700">
                      {t(`app:stool_colors.${selectedLog.stoolColor}`)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="bg-gray-50 p-4 rounded-2xl min-h-[100px]">
            <p className="text-[10px] font-black uppercase text-gray-400 mb-2">{t('app:tracking.notes')}</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              {selectedLog.notes || t('app:tracking.no_notes')}
            </p>
          </div>
          
          <button 
            onClick={() => onDelete(selectedLog.id)}
            className="w-full bg-red-50 text-red-600 p-3 rounded-2xl font-bold hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
          >
            <Trash2 size={16} />
            Supprimer ce log
          </button>
        </div>
      </div>
    </div>
  );
};
